import React, { useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Reusable early-access email capture form.
 * Calls POST /api/waitlist → Brevo.
 *
 * Props:
 *   className  — wrapper div class
 *   inputClass — additional classes for the <input>
 *   buttonText — override button label (default: "Get early access")
 *   source     — optional Brevo TOOL_SOURCE attribute (e.g. "policy-checker")
 */
export default function EarlyAccessForm({ className = "", inputClass = "", buttonText = "Get early access", source = null }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    setError(null);
    try {
      await api.post("/waitlist", { email, ...(source ? { source } : {}) });
      setSubmitted(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  if (submitted) {
    return (
      <div className={`flex items-center gap-2 rounded-lg border border-brand/30 bg-brand/5 px-5 py-3.5 text-sm ${className}`}>
        <CheckCircle2 className="h-4 w-4 text-brand shrink-0" />
        <span>You&apos;re on the list. We&apos;ll be in touch.</span>
      </div>
    );
  }

  return (
    <div className={className}>
      <form onSubmit={handleSubmit} className="flex gap-2" data-testid="early-access-form">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@company.com"
          className={`flex-1 rounded-md border border-border bg-background px-4 py-2.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-brand ${inputClass}`}
          data-testid="early-access-email"
        />
        <button
          type="submit"
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-md bg-brand text-brand-foreground px-5 py-2.5 text-sm font-medium hover:opacity-90 disabled:opacity-60 transition-opacity whitespace-nowrap"
          data-testid="early-access-submit"
        >
          {loading ? "…" : buttonText}
        </button>
      </form>
      {error && <p className="mt-2 text-xs text-rose-600">{error}</p>}
    </div>
  );
}
